import { useSelector, useDispatch } from "../../../redux/hooks";
import { useEffect } from "react";
import dayjs from "dayjs";
import { setDeisolatedTimeModalOpen } from "../../../redux/slices/generatorFeaturesSlice";
import { TDeisolatedKeys } from "../../../redux/slices/generatorSlice";
import { useFormContext } from "react-hook-form";

interface Props {
  tag: string | undefined;
  value: boolean;
}
/**
 * Custom hook to manage the deisolated time modal state based on specific conditions.
 * The modal should only show if at least one deisolated checkbox is unchecked.
 *
 * @param tag - A string representing the tag to check for specific conditions.
 * @param value - A boolean indicating the current state of the tag.
 */
const useDeisolatedTime = ({ tag, value }: Props) => {
  const dispatch = useDispatch();
  const { lbentry } = useSelector(state => state.generatorSlice);
  const { deisolatedTimeModalData } = useSelector(
    state => state.generatorFeaturesSlice,
  );
  const formContext = useFormContext();

  useEffect(() => {
    // Only proceed if this is a deisolated-related checkbox
    if (tag !== "ambDesiolated") return;

    const currentTime = dayjs();

    const targetTimes = [
      dayjs().hour(20).minute(30).second(0),
      dayjs().hour(17).minute(0).second(0),
    ]; // !!!must be the same as TDeisolatedKeys!!!
    let matchedTarget = null;

    targetTimes.forEach(targetTime => {
      const key = targetTime.format("HHmm") as TDeisolatedKeys;
      const isAcknowledged = lbentry[`deisolate${key}`];
      const isMatching = currentTime.isAfter(targetTime) && !isAcknowledged;
      if (isMatching) matchedTarget = key;
    }); // Only show modal if we have a matched target time AND at least one deisolated checkbox is unchecked
    if (matchedTarget) {
      let hasUncheckedDeisolatedCheckbox = false;

      if (formContext?.getValues) {
        try {
          const formValues = formContext.getValues();

          // Look through all form values to find deisolated checkboxes
          Object.keys(formValues).forEach(fieldId => {
            const fieldValue = formValues[fieldId];

            if (typeof fieldValue === "boolean" && fieldValue === false) {
              hasUncheckedDeisolatedCheckbox = true;
            }
          });
        } catch (error) {
          console.warn("Error checking form values:", error);
          hasUncheckedDeisolatedCheckbox = !value;
        }
      } else {
        hasUncheckedDeisolatedCheckbox = !value;
      }

      if (hasUncheckedDeisolatedCheckbox) {
        dispatch(setDeisolatedTimeModalOpen({ timeKey: matchedTarget }));
      } else {
        // All deisolated checkboxes are checked, close the modal
        dispatch(setDeisolatedTimeModalOpen(null));
      }
    } else {
      // No matched target time, close modal
      dispatch(setDeisolatedTimeModalOpen(null));
    }
  }, [tag, value, lbentry, dispatch, formContext]);
};

export default useDeisolatedTime;
